import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { logError } from '../Lib/logger';

/**
 * New Places widget: `PlaceAutocompleteElement` (see migration overview).
 * Legacy `Autocomplete` is deprecated for new Google Cloud projects.
 *
 * https://developers.google.com/maps/documentation/javascript/legacy/places-migration-overview
 */

const GOOGLE_MAPS_SCRIPT_BASE_URL = 'https://maps.googleapis.com/maps/api';
const isBrowser = typeof window !== 'undefined' && window.document;

/**
 * Monkey-patch `Element.prototype.attachShadow` once so that any `gmp-*`
 * element that creates a shadow root (open OR closed) gets our focus-reset
 * stylesheet injected immediately — before Google's own shadow styles run.
 *
 * External CSS cannot override `:host(:focus-within)` rules declared inside a
 * Shadow DOM (even with `!important`), so this interception is the only
 * reliable way to suppress the blue border Google's widget applies on focus.
 */
const patchGmpShadowAttach = (): void => {
  if (!isBrowser) return;
  const win = window as unknown as { __gmpShadowPatchApplied?: boolean };
  if (win.__gmpShadowPatchApplied) return;
  win.__gmpShadowPatchApplied = true;

  // Track which shadow roots were intercepted for debugging
  const captured: string[] = [];
  (win as unknown as { __gmpCapturedShadowRoots?: string[] }).__gmpCapturedShadowRoots = captured;

  const original = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function attachShadow(init) {
    const shadowRoot = original.call(this, init);
    const tag = this.tagName?.toLowerCase() ?? '';
    captured.push(tag);
    // Target gmp-* elements and any element that is a descendant of one —
    // Google Maps nests multiple shadow roots (e.g. internal lit components)
    // that also need the focus reset.
    const style = document.createElement('style');
    // The widget renders a <dialog> whose UA default is border: 2px solid —
    // that is the source of the blue focus border. Must be reset here.
    style.textContent = [
      ':host, :host(:focus), :host(:focus-within), :host(:focus-visible) {',
      '  border: none !important;',
      '  outline: none !important;',
      '  box-shadow: none !important;',
      '}',
      'dialog, dialog[open], dialog:focus, dialog:focus-within {',
      '  border: none !important;',
      '  outline: none !important;',
      '  box-shadow: none !important;',
      '}',
      'input, input:focus, input:focus-visible {',
      '  border: none !important;',
      '  outline: none !important;',
      '  box-shadow: none !important;',
      '}',
      // Google renders an absolutely-positioned overlay div with inline
      // border: 2px solid #1a73e8 to draw the focus ring. !important in a
      // stylesheet overrides inline styles without !important.
      '.focus-ring {',
      '  border: none !important;',
      '  outline: none !important;',
      '  box-shadow: none !important;',
      '}',
    ].join('\n');
    shadowRoot.prepend(style);
    return shadowRoot;
  };
};

const isPlacesLibraryRequestedInScriptUrl = (scriptSource: string): boolean => {
  try {
    const libraries = new URL(scriptSource).searchParams.get('libraries');
    if (!libraries) return false;
    return libraries.split(',').map(s => s.trim()).includes('places');
  } catch {
    return false;
  }
};

const isPlaceAutocompleteElementReady = (): boolean =>
  typeof google !== 'undefined'
  && !!google.maps?.places
  && typeof google.maps.places.PlaceAutocompleteElement === 'function';

const waitForPlaceAutocompleteElement = (timeoutMilliseconds: number): Promise<void> => {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      if (isPlaceAutocompleteElementReady()) {
        resolve();
        return;
      }
      if (Date.now() - start > timeoutMilliseconds) {
        reject(
          new Error(
            'Google Maps Places (PlaceAutocompleteElement) did not load. Use libraries=places on the script URL.',
          ),
        );
        return;
      }
      window.requestAnimationFrame(tick);
    };
    tick();
  });
};

const loadGoogleMapsScript = async (apiKey: string): Promise<void> => {
  patchGmpShadowAttach();
  if (!isBrowser) return;

  if (isPlaceAutocompleteElementReady()) return;

  const existingScripts = document.querySelectorAll<HTMLScriptElement>(
    `script[src*="${GOOGLE_MAPS_SCRIPT_BASE_URL}"]`,
  );

  for (const scriptElement of existingScripts) {
    if (!isPlacesLibraryRequestedInScriptUrl(scriptElement.src)) {
      continue;
    }
    await waitForPlaceAutocompleteElement(15000);
    return;
  }

  const scriptUrl = new URL(`${GOOGLE_MAPS_SCRIPT_BASE_URL}/js`);
  scriptUrl.searchParams.set('libraries', 'places');
  scriptUrl.searchParams.set('key', apiKey);
  scriptUrl.searchParams.set('callback', '__GOOGLE_PLACES_CALLBACK__');
  scriptUrl.searchParams.set('loading', 'async');

  const script = document.createElement('script');
  script.src = scriptUrl.toString();
  script.async = true;

  await new Promise<void>((resolve, reject) => {
    const previousCallback = (window as unknown as { __GOOGLE_PLACES_CALLBACK__?: () => void }).__GOOGLE_PLACES_CALLBACK__;
    (window as unknown as { __GOOGLE_PLACES_CALLBACK__?: () => void }).__GOOGLE_PLACES_CALLBACK__ = () => {
      if (typeof previousCallback === 'function') {
        previousCallback();
      }
      resolve();
    };
    script.addEventListener('error', () => {
      reject(new Error('Failed to load Google Maps JavaScript API'));
    });
    document.body.appendChild(script);
  });

  await waitForPlaceAutocompleteElement(15000);
};

type MappedAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
};

/**
 * US-only flows do not need a country suffix on `formattedAddress`; strip trailing
 * ", USA" / ", United States" / ", US" when the place is in the United States.
 */
const stripUnitedStatesCountrySuffixFromFormattedAddress = (
  formattedAddress: string,
  addressComponents: MappedAddressComponent[],
): string => {
  const countryComponent = addressComponents.find(component => component.types.includes('country'));
  const isUnitedStatesPlace = countryComponent?.short_name === 'US' || countryComponent?.long_name === 'United States';
  const trimmed = formattedAddress.trim();
  const looksLikeTrailingUnitedStatesSuffix = /,?\s*(United States|USA|US)\s*$/i.test(trimmed);
  if (!isUnitedStatesPlace && !(countryComponent === undefined && looksLikeTrailingUnitedStatesSuffix)) {
    return formattedAddress;
  }
  let result = trimmed;
  const trailingCountryPatterns = [
    /,?\s*United States\s*$/i,
    /,?\s*USA\s*$/i,
    /,?\s*US\s*$/i,
  ];
  for (const pattern of trailingCountryPatterns) {
    if (pattern.test(result)) {
      result = result.replace(pattern, '').trim();
      break;
    }
  }
  return result;
};

const mapPlaceToPlaceResult = (place: google.maps.places.Place): google.maps.places.PlaceResult => {
  const addressComponents: MappedAddressComponent[] = place.addressComponents?.map(component => ({
    long_name: component.longText ?? '',
    short_name: component.shortText ?? '',
    types: component.types ?? [],
  })) ?? [];
  const formatted_address = stripUnitedStatesCountrySuffixFromFormattedAddress(
    place.formattedAddress ?? '',
    addressComponents,
  );
  const location = place.location ?? undefined;
  return {
    address_components: addressComponents,
    formatted_address,
    ...(location ? { geometry: { location } } : {}),
  };
};

/**
 * Reads numeric latitude/longitude from a legacy-shaped `PlaceResult` produced by
 * {@link mapPlaceToPlaceResult} (geometry.location from the Places API `Place` class).
 */
export const getLatitudeLongitudeFromPlaceResult = (
  place: google.maps.places.PlaceResult,
): { latitude: number; longitude: number } | null => {
  const location = place.geometry?.location;
  if (!location) return null;
  const latitude = typeof location.lat === 'function' ? location.lat() : location.lat;
  const longitude = typeof location.lng === 'function' ? location.lng() : location.lng;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
  return { latitude, longitude };
};

const primaryTypesFromLegacyAddressType = (types?: string[]): string[] | undefined => {
  if (!types || types.length === 0) {
    return ['street_address', 'premise', 'subpremise'];
  }
  if (types.includes('address')) {
    return ['street_address', 'premise', 'subpremise'];
  }
  return undefined;
};

/** Same shape as legacy `PlaceResult` after mapping from the new `Place` class. */
export type GooglePlacesSelectedPlace = google.maps.places.PlaceResult;

export type UseGooglePlacesAutocompleteOptions = {
  apiKey: string;
  controlledValue: string;
  onInputChange: (value: string) => void;
  onPlaceSelected?: (place: GooglePlacesSelectedPlace) => void;
  placeholder?: string;
  options?: {
    types?: string[];
    componentRestrictions?: { country: string | string[] };
    fields?: string[];
  };
};

export const useGooglePlacesAutocomplete = ({
  apiKey,
  controlledValue,
  onInputChange,
  onPlaceSelected,
  placeholder,
  options = {},
}: UseGooglePlacesAutocompleteOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const onInputChangeRef = useRef(onInputChange);
  onPlaceSelectedRef.current = onPlaceSelected;
  onInputChangeRef.current = onInputChange;

  const {
    types,
    componentRestrictions,
  } = options;

  // Serialize to JSON so new object/array literals passed on every render don't
  // cause useMemo to produce new references, which would retrigger useLayoutEffect
  // and destroy + recreate the PlaceAutocompleteElement on every keystroke.
  const typesJson = JSON.stringify(types);
  const countryJson = JSON.stringify(componentRestrictions?.country);

  const includedPrimaryTypes = useMemo(
    () => primaryTypesFromLegacyAddressType(types),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typesJson],
  );

  const includedRegionCodes = useMemo(() => {
    const country = componentRestrictions?.country;
    if (Array.isArray(country)) {
      return country.map(c => c.toUpperCase());
    }
    if (country) {
      return [country.toUpperCase()];
    }
    return ['US'];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryJson]);

  useLayoutEffect(() => {
    if (!apiKey || !containerRef.current) return;
    let cancelled = false;

    const setup = async () => {
      try {
        await loadGoogleMapsScript(apiKey);
      } catch (error) {
        logError('Google Maps script failed to load', error);
        return;
      }
      if (cancelled || !containerRef.current) return;

      const PlaceAutocompleteElement = google.maps.places?.PlaceAutocompleteElement;
      if (!PlaceAutocompleteElement) {
        logError(
          'PlaceAutocompleteElement is missing after load',
          new Error('PlaceAutocompleteElement unavailable'),
        );
        return;
      }

      const elementOptions: google.maps.places.PlaceAutocompleteElementOptions = {
        includedRegionCodes,
        ...(includedPrimaryTypes ? { includedPrimaryTypes } : {}),
        ...(placeholder ? { placeholder } : {}),
      };

      const element = new PlaceAutocompleteElement(elementOptions);
      element.style.width = '100%';
      element.style.height = '100%';
      element.style.boxSizing = 'border-box';
      element.style.border = 'none';
      element.style.borderRadius = '0';
      element.style.background = 'transparent';
      element.style.outline = 'none';
      element.style.boxShadow = 'none';
      widgetRef.current = element;

      const handleGmpSelect = async (event: Event) => {
        const placePrediction = (event as google.maps.places.PlacePredictionSelectEvent).placePrediction;
        if (!placePrediction) return;
        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress', 'location'],
        });
        onPlaceSelectedRef.current?.(mapPlaceToPlaceResult(place));
      };

      const handleInput = () => {
        onInputChangeRef.current(element.value);
      };

      element.addEventListener('gmp-select', handleGmpSelect);
      element.addEventListener('input', handleInput);

      containerRef.current.appendChild(element);
    };

    setup();

    return () => {
      cancelled = true;
      const widget = widgetRef.current;
      if (widget) {
        widget.remove();
        widgetRef.current = null;
      }
    };
  }, [apiKey, includedPrimaryTypes, includedRegionCodes, placeholder]);

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;
    if (widget.value !== controlledValue) {
      widget.value = controlledValue;
    }
  }, [controlledValue]);

  return { containerRef };
};
